import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Paper, Typography, Chip } from "@mui/material";
import {
  ChatBubbleOutline as ChatIcon,
  Terminal as SystemIcon,
  Person as UserIcon,
} from "@mui/icons-material";
import PromptForm from "../../components/forms/PromptForm";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import { Prompt } from "../../types";
import { formatDateTime } from "../../utils/formatters";

const PromptDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const alert = useAlert();
  const isNew = id === "new";

  // State variables
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState<boolean>(!isNew);
  const [error, setError] = useState<string | null>(null);

  // Fetch prompt if editing
  useEffect(() => {
    if (isNew) return;

    const fetchPrompt = async () => {
      try {
        setLoading(true);
        const response = await api.prompts.getById(id as string);
        setPrompt(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching prompt:", error);
        setError("Failed to load prompt. Please try again.");
        alert.error("Failed to fetch prompt");
      } finally {
        setLoading(false);
      }
    };

    fetchPrompt();
  }, [id, isNew, alert]);

  // Get prompt type icon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case "system":
        return <SystemIcon fontSize="small" />;
      case "user":
        return <UserIcon fontSize="small" />;
      case "chat":
      default:
        return <ChatIcon fontSize="small" />;
    }
  };

  return (
    <Box>
      <LoadingBackdrop open={loading && !prompt && !isNew} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      {!isNew && prompt && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5" fontWeight="medium">
                  {prompt.name}
                </Typography>
                <Chip
                  label={prompt.type}
                  color={
                    prompt.type === "system"
                      ? "primary"
                      : prompt.type === "user"
                        ? "secondary"
                        : "info"
                  }
                  size="small"
                  icon={getTypeIcon(prompt.type)}
                  sx={{ textTransform: "capitalize" }}
                />
                {prompt.response_format && (
                  <Chip
                    label="Response Format"
                    color="success"
                    size="small"
                    sx={{ textTransform: "capitalize" }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Last updated: {formatDateTime(prompt.updatedAt)}
              </Typography>
            </Box>
          </Box>
          {prompt.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {prompt.description}
            </Typography>
          )}
        </Paper>
      )}

      {(isNew || prompt) && (
        <PromptForm initialData={prompt || undefined} isEdit={!isNew} />
      )}
    </Box>
  );
};

export default PromptDetail;
